import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Search,
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Copy,
  Merge,
  Image,
  Hash,
  Package,
  Upload,
  X,
  FileText,
  Eye,
  Trash2,
  Link,
  Database,
  Shield,
  Plus,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProductDataManager = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  
  // Duplicates state
  const [duplicates, setDuplicates] = useState([]);
  const [duplicateType, setDuplicateType] = useState("sku");
  const [duplicatePage, setDuplicatePage] = useState(1);
  const [duplicateTotal, setDuplicateTotal] = useState(0);
  const [duplicatePages, setDuplicatePages] = useState(1);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [mergeStrategy, setMergeStrategy] = useState("keep_primary");
  const [primaryProductId, setPrimaryProductId] = useState(null);
  const [merging, setMerging] = useState(false);
  
  // Duplicate filters state
  const [duplicateSearch, setDuplicateSearch] = useState("");
  const [duplicateCollection, setDuplicateCollection] = useState("");
  const [duplicateCollections, setDuplicateCollections] = useState([]);
  
  // Bulk merge state
  const [selectedDuplicateGroups, setSelectedDuplicateGroups] = useState([]);
  const [showBulkMergeDialog, setShowBulkMergeDialog] = useState(false);
  const [bulkMergeStrategy, setBulkMergeStrategy] = useState("keep_primary");
  const [selectionCriteria, setSelectionCriteria] = useState("oldest");
  const [bulkMerging, setBulkMerging] = useState(false);
  const [autoSelections, setAutoSelections] = useState([]);
  
  // Missing SKUs state
  const [missingSkuProducts, setMissingSkuProducts] = useState([]);
  const [missingSkuPage, setMissingSkuPage] = useState(1);
  const [missingSkuTotal, setMissingSkuTotal] = useState(0);
  const [missingSkuPages, setMissingSkuPages] = useState(1);
  const [skuSearch, setSkuSearch] = useState("");
  const [skuCollection, setSkuCollection] = useState("");
  const [skuCollections, setSkuCollections] = useState([]);
  const [editingSkuProduct, setEditingSkuProduct] = useState(null);
  const [newSku, setNewSku] = useState("");
  const [assigningSku, setAssigningSku] = useState(false);
  
  // Bulk SKU upload state
  const [showBulkSkuDialog, setShowBulkSkuDialog] = useState(false);
  const [bulkSkuText, setBulkSkuText] = useState("");
  const [bulkSkuUploading, setBulkSkuUploading] = useState(false);
  
  // CSV SKU upload state
  const [showCsvSkuDialog, setShowCsvSkuDialog] = useState(false);
  const [csvSkuJobId, setCsvSkuJobId] = useState(null);
  const [csvSkuStatus, setCsvSkuStatus] = useState(null);
  const [csvColumns, setCsvColumns] = useState([]);
  const [nameColumn, setNameColumn] = useState("name");
  const [skuColumnSelect, setSkuColumnSelect] = useState("sku");
  const [csvFile, setCsvFile] = useState(null);
  
  // Missing Images state
  const [missingImageProducts, setMissingImageProducts] = useState([]);
  const [missingImagePage, setMissingImagePage] = useState(1);
  const [missingImageTotal, setMissingImageTotal] = useState(0);
  const [missingImagePages, setMissingImagePages] = useState(1);
  const [imageSearch, setImageSearch] = useState("");
  const [imageCollection, setImageCollection] = useState("");
  const [imageCollections, setImageCollections] = useState([]);
  const [editingImageProduct, setEditingImageProduct] = useState(null);
  const [newImageUrls, setNewImageUrls] = useState("");
  const [addingImages, setAddingImages] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Fetch summary
  const fetchSummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/products/data-quality-summary`);
      setSummary(response.data);
    } catch (err) {
      toast.error("Failed to fetch data quality summary");
    }
    setLoading(false);
  }, []);

  // Fetch duplicates
  const fetchDuplicates = useCallback(async () => {
    try {
      const params = { duplicate_type: duplicateType, page: duplicatePage, limit: 20 };
      if (duplicateSearch) params.search = duplicateSearch;
      if (duplicateCollection) params.collection = duplicateCollection;
      
      const response = await axios.get(`${API}/admin/products/duplicates`, { params });
      setDuplicates(response.data.duplicates);
      setDuplicateTotal(response.data.total_groups);
      setDuplicatePages(response.data.pages);
      setDuplicateCollections(response.data.collections || []);
    } catch (err) {
      toast.error("Failed to fetch duplicates");
    }
  }, [duplicateType, duplicatePage, duplicateSearch, duplicateCollection]);

  // Fetch missing SKUs
  const fetchMissingSkus = useCallback(async () => {
    try {
      const params = { page: missingSkuPage, limit: 25 };
      if (skuSearch) params.search = skuSearch;
      if (skuCollection) params.collection = skuCollection;
      
      const response = await axios.get(`${API}/admin/products/missing-skus`, { params });
      setMissingSkuProducts(response.data.products);
      setMissingSkuTotal(response.data.total);
      setMissingSkuPages(response.data.pages);
      setSkuCollections(response.data.collections);
    } catch (err) {
      toast.error("Failed to fetch products with missing SKUs");
    }
  }, [missingSkuPage, skuSearch, skuCollection]);

  // Fetch missing images
  const fetchMissingImages = useCallback(async () => {
    try {
      const params = { page: missingImagePage, limit: 25 };
      if (imageSearch) params.search = imageSearch;
      if (imageCollection) params.collection = imageCollection;
      
      const response = await axios.get(`${API}/admin/products/missing-images`, { params });
      setMissingImageProducts(response.data.products);
      setMissingImageTotal(response.data.total);
      setMissingImagePages(response.data.pages);
      setImageCollections(response.data.collections);
    } catch (err) {
      toast.error("Failed to fetch products with missing images");
    }
  }, [missingImagePage, imageSearch, imageCollection]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    if (activeTab === "duplicates") {
      fetchDuplicates();
    }
  }, [activeTab, fetchDuplicates]);

  useEffect(() => {
    if (activeTab === "missing-skus") {
      fetchMissingSkus();
    }
  }, [activeTab, fetchMissingSkus]);

  useEffect(() => {
    if (activeTab === "missing-images") {
      fetchMissingImages();
    }
  }, [activeTab, fetchMissingImages]);

  // Merge products
  const handleMerge = async () => {
    if (!selectedGroup || !primaryProductId) return;
    
    const secondaryIds = selectedGroup.products
      .filter(p => p.id !== primaryProductId)
      .map(p => p.id);
    
    if (secondaryIds.length === 0) {
      toast.error("Select at least one product to merge");
      return;
    }
    
    setMerging(true);
    try {
      const response = await axios.post(`${API}/admin/products/merge`, {
        primary_product_id: primaryProductId,
        secondary_product_ids: secondaryIds,
        merge_strategy: mergeStrategy
      });
      toast.success(`Merged ${response.data.merged_products} products`);
      setSelectedGroup(null);
      setPrimaryProductId(null);
      fetchDuplicates();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to merge products");
    }
    setMerging(false);
  };

  // Toggle duplicate group selection
  const toggleDuplicateGroupSelection = (group) => {
    setSelectedDuplicateGroups(prev => {
      const exists = prev.find(g => g._id === group._id);
      if (exists) {
        return prev.filter(g => g._id !== group._id);
      }
      return [...prev, group];
    });
  };

  // Select all visible duplicate groups
  const toggleAllDuplicateGroups = () => {
    const allSelected = duplicates.every(d => selectedDuplicateGroups.find(g => g._id === d._id));
    if (allSelected) {
      setSelectedDuplicateGroups(prev => prev.filter(g => !duplicates.find(d => d._id === g._id)));
    } else {
      setSelectedDuplicateGroups(prev => {
        const newSelection = [...prev];
        duplicates.forEach(d => {
          if (!newSelection.find(g => g._id === d._id)) {
            newSelection.push(d);
          }
        });
        return newSelection;
      });
    }
  };

  // Auto-select primary products for bulk merge
  const handleAutoSelectPrimaries = async () => {
    if (selectedDuplicateGroups.length === 0) return;
    
    try {
      const response = await axios.post(`${API}/admin/products/auto-select-primary`, {
        duplicate_groups: selectedDuplicateGroups,
        selection_criteria: selectionCriteria
      });
      setAutoSelections(response.data.selections);
      setShowBulkMergeDialog(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to auto-select primaries");
    }
  };

  // Execute bulk merge
  const handleBulkMerge = async () => {
    if (autoSelections.length === 0) return;
    
    setBulkMerging(true);
    try {
      const response = await axios.post(`${API}/admin/products/bulk-merge`, {
        groups: autoSelections,
        merge_strategy: bulkMergeStrategy,
        selection_criteria: selectionCriteria
      });
      
      toast.success(`Merged ${response.data.total_merged} products from ${response.data.groups_processed} groups`);
      setShowBulkMergeDialog(false);
      setSelectedDuplicateGroups([]);
      setAutoSelections([]);
      fetchDuplicates();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk merge failed");
    }
    setBulkMerging(false);
  };

  // Assign SKU
  const handleAssignSku = async () => {
    if (!editingSkuProduct || !newSku.trim()) return;
    
    setAssigningSku(true);
    try {
      await axios.post(`${API}/admin/products/assign-sku`, {
        product_id: editingSkuProduct.id,
        sku: newSku.trim()
      });
      toast.success(`SKU assigned: ${newSku.trim()}`);
      setEditingSkuProduct(null);
      setNewSku("");
      fetchMissingSkus();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign SKU");
    }
    setAssigningSku(false);
  };

  // Bulk assign SKUs
  const handleBulkSkuAssign = async () => {
    if (!bulkSkuText.trim()) return;
    
    // Parse CSV-like format: "Product Name,SKU" per line
    const lines = bulkSkuText.trim().split("\n");
    const assignments = [];
    
    for (const line of lines) {
      const parts = line.split(",").map(s => s.trim());
      if (parts.length >= 2) {
        assignments.push({
          product_name: parts[0],
          sku: parts[1]
        });
      }
    }
    
    if (assignments.length === 0) {
      toast.error("No valid assignments found. Format: Product Name,SKU");
      return;
    }
    
    setBulkSkuUploading(true);
    try {
      const response = await axios.post(`${API}/admin/products/bulk-assign-skus`, { assignments });
      toast.success(`Assigned ${response.data.assigned} SKUs, ${response.data.failed} failed`);
      if (response.data.errors.length > 0) {
        console.log("SKU assignment errors:", response.data.errors);
      }
      setShowBulkSkuDialog(false);
      setBulkSkuText("");
      fetchMissingSkus();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk SKU assignment failed");
    }
    setBulkSkuUploading(false);
  };

  // CSV SKU upload - handle file selection
  const handleCsvFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setCsvFile(file);
    
    // Read file to detect columns
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split("\n");
      if (lines.length > 0) {
        const headers = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, ""));
        setCsvColumns(headers);
        // Auto-detect name and SKU columns
        const nameCols = headers.filter(h => h.toLowerCase().includes("name") || h.toLowerCase().includes("product"));
        const skuCols = headers.filter(h => h.toLowerCase().includes("sku") || h.toLowerCase().includes("code") || h.toLowerCase().includes("item"));
        if (nameCols.length > 0) setNameColumn(nameCols[0]);
        if (skuCols.length > 0) setSkuColumnSelect(skuCols[0]);
      }
    };
    reader.readAsText(file);
  };

  // CSV SKU upload - start processing
  const handleCsvSkuUpload = async () => {
    if (!csvFile) return;
    
    const formData = new FormData();
    formData.append("file", csvFile);
    formData.append("name_column", nameColumn);
    formData.append("sku_column", skuColumnSelect);
    
    try {
      const response = await axios.post(`${API}/admin/products/upload-sku-csv`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      if (response.data.job_id) {
        setCsvSkuJobId(response.data.job_id);
        setCsvSkuStatus({
          status: "pending",
          total: response.data.total_rows,
          processed: 0,
          assigned: 0,
          progress_percent: 0
        });
        toast.success(`Started processing ${response.data.total_rows} rows`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload CSV");
    }
  };

  // Poll CSV SKU job status
  useEffect(() => {
    if (!csvSkuJobId) return;
    
    const pollStatus = async () => {
      try {
        const response = await axios.get(`${API}/admin/products/sku-csv-status/${csvSkuJobId}`);
        setCsvSkuStatus(response.data);
        
        if (response.data.status === "completed" || response.data.status === "failed") {
          toast.success(`CSV processing complete: ${response.data.assigned} SKUs assigned, ${response.data.not_found} not found`);
          fetchMissingSkus();
          fetchSummary();
        }
      } catch (err) {
        console.error("Failed to poll CSV status:", err);
      }
    };
    
    const interval = setInterval(() => {
      if (csvSkuStatus?.status === "completed" || csvSkuStatus?.status === "failed") {
        clearInterval(interval);
        return;
      }
      pollStatus();
    }, 1000);
    
    pollStatus();
    
    return () => clearInterval(interval);
  }, [csvSkuJobId, csvSkuStatus?.status, fetchMissingSkus, fetchSummary]);

  // Reset CSV dialog
  const resetCsvDialog = () => {
    setShowCsvSkuDialog(false);
    setCsvFile(null);
    setCsvColumns([]);
    setCsvSkuJobId(null);
    setCsvSkuStatus(null);
    setNameColumn("name");
    setSkuColumnSelect("sku");
  };

  // Add images by URL
  const handleAddImages = async () => {
    if (!editingImageProduct || !newImageUrls.trim()) return;
    
    const urls = newImageUrls.split("\n").map(u => u.trim()).filter(u => u);
    
    setAddingImages(true);
    try {
      const response = await axios.post(`${API}/admin/products/add-images`, {
        product_id: editingImageProduct.id,
        image_urls: urls
      });
      toast.success(`Added ${response.data.images_added} images`);
      setEditingImageProduct(null);
      setNewImageUrls("");
      fetchMissingImages();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add images");
    }
    setAddingImages(false);
  };

  // Upload image file
  const handleFileUpload = async (e, productId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingImage(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("product_id", productId);
    
    try {
      const response = await axios.post(`${API}/admin/products/upload-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Image uploaded successfully");
      fetchMissingImages();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload image");
    }
    setUploadingImage(false);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4" data-testid="data-manager-loading">
        <Loader2 className="w-12 h-12 animate-spin text-[#003366]" />
        <p className="text-gray-600">Loading data quality report...</p>
      </div>
    );
  }

  return (
    <div data-testid="product-data-manager" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row gap-4 lg:items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3" data-testid="data-manager-title">
            <Database className="w-8 h-8 text-[#003366]" />
            Product Data Manager
          </h1>
          <p className="text-gray-600 mt-1">
            Clean up duplicates, assign missing SKUs, and add product images
          </p>
        </div>
        <Button onClick={() => { fetchSummary(); }} data-testid="refresh-summary-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Data Quality Score Card */}
      {summary && (
        <div className="bg-gradient-to-r from-[#003366] to-[#004488] rounded-xl p-6 text-white" data-testid="quality-score-card">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <p className="text-sm opacity-80">Data Quality Score</p>
              <div className="flex items-center gap-4 mt-1">
                <span className="text-5xl font-bold">{summary.data_quality_score}%</span>
                <Shield className="w-10 h-10 opacity-60" />
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-xs opacity-80">Total Products</p>
                <p className="text-2xl font-bold">{summary.total_products}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-xs opacity-80">Missing SKU</p>
                <p className="text-2xl font-bold text-orange-300">{summary.missing_sku}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-xs opacity-80">Missing Images</p>
                <p className="text-2xl font-bold text-orange-300">{summary.missing_images}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="text-xs opacity-80">Duplicates</p>
                <p className="text-2xl font-bold text-red-300">{summary.duplicate_skus + summary.duplicate_names}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-4 w-full max-w-xl">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="duplicates" className="flex items-center gap-2">
            <Copy className="w-4 h-4" />
            Duplicates
            {summary?.duplicate_skus > 0 && (
              <Badge variant="destructive" className="ml-1 text-xs">{summary.duplicate_skus}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="missing-skus" className="flex items-center gap-2">
            <Hash className="w-4 h-4" />
            Missing SKUs
            {summary?.missing_sku > 0 && (
              <Badge variant="secondary" className="ml-1 text-xs">{summary.missing_sku}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="missing-images" className="flex items-center gap-2">
            <Image className="w-4 h-4" />
            Missing Images
            {summary?.missing_images > 0 && (
              <Badge variant="secondary" className="ml-1 text-xs">{summary.missing_images}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Issue Cards */}
            {summary?.duplicate_skus > 0 && (
              <div className="bg-white rounded-xl border border-red-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
                   onClick={() => setActiveTab("duplicates")} data-testid="duplicate-card">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <Copy className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Duplicate SKUs</p>
                    <p className="text-sm text-gray-500">{summary.duplicate_skus} groups found</p>
                  </div>
                </div>
                <p className="text-sm text-red-600">Click to review and merge</p>
              </div>
            )}
            
            {summary?.missing_sku > 0 && (
              <div className="bg-white rounded-xl border border-orange-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
                   onClick={() => setActiveTab("missing-skus")} data-testid="missing-sku-card">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                    <Hash className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Missing SKUs</p>
                    <p className="text-sm text-gray-500">{summary.missing_sku} products</p>
                  </div>
                </div>
                <p className="text-sm text-orange-600">Click to assign SKUs</p>
              </div>
            )}
            
            {summary?.missing_images > 0 && (
              <div className="bg-white rounded-xl border border-yellow-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
                   onClick={() => setActiveTab("missing-images")} data-testid="missing-images-card">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                    <Image className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Missing Images</p>
                    <p className="text-sm text-gray-500">{summary.missing_images} products</p>
                  </div>
                </div>
                <p className="text-sm text-yellow-600">Click to add images</p>
              </div>
            )}
            
            {summary?.duplicate_skus === 0 && summary?.missing_sku === 0 && summary?.missing_images === 0 && (
              <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-6 col-span-full">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                  <div>
                    <p className="font-semibold text-emerald-800">All Clear!</p>
                    <p className="text-sm text-emerald-700">No data quality issues found. Your product catalog is in great shape.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Duplicates Tab */}
        <TabsContent value="duplicates" className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            {/* Filters Row */}
            <div className="flex flex-wrap items-center gap-4 mb-4">
              {/* Search */}
              <div className="relative flex-1 max-w-xs">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  value={duplicateSearch}
                  onChange={(e) => { setDuplicateSearch(e.target.value); setDuplicatePage(1); }}
                  placeholder="Search by name or SKU..."
                  className="pl-9"
                  data-testid="duplicate-search-input"
                />
              </div>
              
              {/* Duplicate Type */}
              <Select value={duplicateType} onValueChange={(v) => { setDuplicateType(v); setDuplicatePage(1); setSelectedDuplicateGroups([]); }}>
                <SelectTrigger className="w-32" data-testid="duplicate-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sku">By SKU</SelectItem>
                  <SelectItem value="name">By Name</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Collection Filter */}
              <Select value={duplicateCollection} onValueChange={(v) => { setDuplicateCollection(v === "all" ? "" : v); setDuplicatePage(1); }}>
                <SelectTrigger className="w-48" data-testid="duplicate-collection-select">
                  <SelectValue placeholder="All Collections" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Collections</SelectItem>
                  {duplicateCollections.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Badge variant="outline">{duplicateTotal} groups</Badge>
            </div>
            
            {/* Bulk Action Bar */}
            {selectedDuplicateGroups.length > 0 && (
              <div className="flex items-center gap-3 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200" data-testid="bulk-action-bar">
                <Badge variant="default" className="bg-blue-600">{selectedDuplicateGroups.length} selected</Badge>
                <Select value={selectionCriteria} onValueChange={setSelectionCriteria}>
                  <SelectTrigger className="w-40" data-testid="selection-criteria-select">
                    <SelectValue placeholder="Keep..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="oldest">Keep Oldest</SelectItem>
                    <SelectItem value="newest">Keep Newest</SelectItem>
                    <SelectItem value="most_images">Keep Most Images</SelectItem>
                    <SelectItem value="most_data">Keep Most Data</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={handleAutoSelectPrimaries} className="bg-blue-600 hover:bg-blue-700" data-testid="bulk-merge-btn">
                  <Merge className="w-4 h-4 mr-2" />
                  Bulk Merge
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelectedDuplicateGroups([])} data-testid="clear-selection-btn">
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              </div>
            )}
            
            {duplicates.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-emerald-500" />
                <p>No duplicates found {duplicateSearch ? `matching "${duplicateSearch}"` : `by ${duplicateType}`}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Select All */}
                <div className="flex items-center gap-2 pb-2 border-b">
                  <input
                    type="checkbox"
                    checked={duplicates.length > 0 && duplicates.every(d => selectedDuplicateGroups.find(g => g._id === d._id))}
                    onChange={toggleAllDuplicateGroups}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-600">Select all on this page</span>
                </div>
                
                {duplicates.map((group, idx) => {
                  const isSelected = selectedDuplicateGroups.find(g => g._id === group._id);
                  return (
                    <div 
                      key={idx} 
                      className={`border rounded-lg p-4 transition-colors ${isSelected ? 'border-blue-400 bg-blue-50/50' : 'border-gray-200'}`}
                      data-testid={`duplicate-group-${idx}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={!!isSelected}
                            onChange={() => toggleDuplicateGroupSelection(group)}
                            className="rounded"
                          />
                          <Badge variant="destructive">{group.count} duplicates</Badge>
                          <span className="text-sm text-gray-600 truncate max-w-[300px]">
                            {duplicateType === "sku" ? `SKU: ${group._id}` : `"${group._id}"`}
                          </span>
                          {group.collections?.length > 0 && (
                            <Badge variant="outline" className="text-xs">
                              {group.collections.length} collection{group.collections.length > 1 ? 's' : ''}
                            </Badge>
                          )}
                        </div>
                        <Button size="sm" variant="outline" onClick={() => { setSelectedGroup(group); setPrimaryProductId(group.products[0]?.id); }}>
                          <Eye className="w-4 h-4 mr-2" />
                          Review
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                        {group.products.slice(0, 4).map((product, pidx) => (
                          <div key={pidx} className="flex items-center gap-2 p-2 bg-white border border-gray-100 rounded text-sm">
                            <div className="w-10 h-10 bg-gray-100 rounded flex-shrink-0 overflow-hidden">
                              {product.images?.[0] ? (
                                <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Package className="w-4 h-4 text-gray-400" />
                                </div>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="truncate font-medium text-gray-900">{product.name}</p>
                              <p className="text-xs text-gray-500">{product.collection_name}</p>
                            </div>
                          </div>
                        ))}
                        {group.products.length > 4 && (
                          <div className="flex items-center justify-center p-2 bg-gray-50 rounded text-sm text-gray-500 border border-dashed border-gray-200">
                            +{group.products.length - 4} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Pagination */}
            {duplicatePages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-gray-600">Page {duplicatePage} of {duplicatePages}</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={duplicatePage <= 1} onClick={() => setDuplicatePage(p => p - 1)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" disabled={duplicatePage >= duplicatePages} onClick={() => setDuplicatePage(p => p + 1)}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Missing SKUs Tab */}
        <TabsContent value="missing-skus" className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <div className="relative flex-1 max-w-xs">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  value={skuSearch}
                  onChange={(e) => { setSkuSearch(e.target.value); setMissingSkuPage(1); }}
                  placeholder="Search products..."
                  className="pl-9"
                  data-testid="sku-search-input"
                />
              </div>
              <Select value={skuCollection} onValueChange={(v) => { setSkuCollection(v === "all" ? "" : v); setMissingSkuPage(1); }}>
                <SelectTrigger className="w-48" data-testid="sku-collection-select">
                  <SelectValue placeholder="All Collections" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Collections</SelectItem>
                  {skuCollections.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={() => setShowBulkSkuDialog(true)} data-testid="bulk-sku-btn">
                <FileText className="w-4 h-4 mr-2" />
                Paste SKUs
              </Button>
              <Button variant="default" onClick={() => setShowCsvSkuDialog(true)} data-testid="csv-sku-btn" className="bg-[#003366] hover:bg-[#004488]">
                <Upload className="w-4 h-4 mr-2" />
                Upload CSV
              </Button>
              <Badge variant="outline">{missingSkuTotal} products</Badge>
            </div>
            
            {missingSkuProducts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-emerald-500" />
                <p>All products have SKUs assigned</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Product</th>
                      <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Collection</th>
                      <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Price</th>
                      <th className="p-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {missingSkuProducts.map((product, idx) => (
                      <tr key={product.id} className="hover:bg-gray-50" data-testid={`missing-sku-row-${idx}`}>
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                              {product.images?.[0] ? (
                                <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Package className="w-4 h-4 text-gray-400" />
                                </div>
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 truncate max-w-[300px]">{product.name}</p>
                              <p className="text-xs text-red-500">No SKU</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-3 text-sm text-gray-600">{product.collection_name}</td>
                        <td className="p-3 text-sm text-gray-900">{product.price}</td>
                        <td className="p-3 text-center">
                          <Button size="sm" variant="outline" onClick={() => { setEditingSkuProduct(product); setNewSku(""); }}>
                            <Hash className="w-4 h-4 mr-1" />
                            Assign SKU
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination */}
            {missingSkuPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-gray-600">Showing {((missingSkuPage - 1) * 25) + 1} - {Math.min(missingSkuPage * 25, missingSkuTotal)} of {missingSkuTotal}</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={missingSkuPage <= 1} onClick={() => setMissingSkuPage(p => p - 1)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" disabled={missingSkuPage >= missingSkuPages} onClick={() => setMissingSkuPage(p => p + 1)}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Missing Images Tab */}
        <TabsContent value="missing-images" className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <div className="relative flex-1 max-w-xs">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  value={imageSearch}
                  onChange={(e) => { setImageSearch(e.target.value); setMissingImagePage(1); }}
                  placeholder="Search products..."
                  className="pl-9"
                  data-testid="image-search-input"
                />
              </div>
              <Select value={imageCollection} onValueChange={(v) => { setImageCollection(v === "all" ? "" : v); setMissingImagePage(1); }}>
                <SelectTrigger className="w-48" data-testid="image-collection-select">
                  <SelectValue placeholder="All Collections" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Collections</SelectItem>
                  {imageCollections.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Badge variant="outline">{missingImageTotal} products</Badge>
            </div>
            
            {missingImageProducts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-emerald-500" />
                <p>All products have images</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {missingImageProducts.map((product, idx) => (
                  <div key={product.id} className="border border-gray-200 rounded-lg p-4" data-testid={`missing-image-card-${idx}`}>
                    <div className="w-full h-32 bg-gray-100 rounded-lg mb-3 flex items-center justify-center">
                      <Image className="w-12 h-12 text-gray-300" />
                    </div>
                    <p className="font-medium text-gray-900 truncate mb-1">{product.name}</p>
                    <p className="text-xs text-gray-500 mb-3">{product.collection_name} • {product.sku || "No SKU"}</p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => { setEditingImageProduct(product); setNewImageUrls(""); }}>
                        <Link className="w-4 h-4 mr-1" />
                        Add URL
                      </Button>
                      <label className="flex-1">
                        <Button size="sm" variant="default" className="w-full" asChild disabled={uploadingImage}>
                          <span>
                            {uploadingImage ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                            Upload
                          </span>
                        </Button>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileUpload(e, product.id)}
                        />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Pagination */}
            {missingImagePages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-gray-600">Showing {((missingImagePage - 1) * 25) + 1} - {Math.min(missingImagePage * 25, missingImageTotal)} of {missingImageTotal}</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={missingImagePage <= 1} onClick={() => setMissingImagePage(p => p - 1)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" disabled={missingImagePage >= missingImagePages} onClick={() => setMissingImagePage(p => p + 1)}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Merge Dialog */}
      <Dialog open={!!selectedGroup} onOpenChange={(open) => !open && setSelectedGroup(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Merge className="w-5 h-5" />
              Merge Duplicate Products
            </DialogTitle>
            <DialogDescription>
              Select the primary product to keep and choose a merge strategy
            </DialogDescription>
          </DialogHeader>
          {selectedGroup && (
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Merge Strategy</label>
                <Select value={mergeStrategy} onValueChange={setMergeStrategy}>
                  <SelectTrigger data-testid="merge-strategy-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="keep_primary">Keep Primary (merge images only)</SelectItem>
                    <SelectItem value="merge_all">Merge All Data (fill empty fields)</SelectItem>
                    <SelectItem value="select_best">Select Best (use most complete)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Select Primary Product (will be kept)</label>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {selectedGroup.products.map((product, idx) => (
                    <div
                      key={product.id}
                      className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                        primaryProductId === product.id ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300"
                      }`}
                      onClick={() => setPrimaryProductId(product.id)}
                      data-testid={`merge-product-${idx}`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          checked={primaryProductId === product.id}
                          onChange={() => setPrimaryProductId(product.id)}
                          className="w-4 h-4"
                        />
                        <div className="w-12 h-12 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                          {product.images?.[0] && (
                            <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{product.name}</p>
                          <p className="text-xs text-gray-500">
                            {product.collection_name} • {product.images?.length || 0} images • {product.sku || "No SKU"}
                          </p>
                        </div>
                        {primaryProductId === product.id && (
                          <Badge className="bg-blue-500">Primary</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
                <p className="font-medium text-yellow-800">Warning</p>
                <p className="text-yellow-700">
                  {selectedGroup.products.length - 1} product(s) will be deleted and merged into the primary product.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedGroup(null)}>Cancel</Button>
            <Button onClick={handleMerge} disabled={merging || !primaryProductId} data-testid="confirm-merge-btn">
              {merging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Merge className="w-4 h-4 mr-2" />}
              Merge Products
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign SKU Dialog */}
      <Dialog open={!!editingSkuProduct} onOpenChange={(open) => !open && setEditingSkuProduct(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Hash className="w-5 h-5" />
              Assign SKU
            </DialogTitle>
            <DialogDescription>
              Enter the SKU for: <span className="font-semibold">{editingSkuProduct?.name}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">SKU</label>
              <Input
                value={newSku}
                onChange={(e) => setNewSku(e.target.value)}
                placeholder="Enter SKU (e.g., ABC-12345)"
                data-testid="assign-sku-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingSkuProduct(null)}>Cancel</Button>
            <Button onClick={handleAssignSku} disabled={assigningSku || !newSku.trim()} data-testid="confirm-assign-sku-btn">
              {assigningSku ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk SKU Upload Dialog */}
      <Dialog open={showBulkSkuDialog} onOpenChange={setShowBulkSkuDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Bulk Upload SKUs
            </DialogTitle>
            <DialogDescription>
              Paste product names and SKUs (one per line, comma-separated)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Format: Product Name,SKU</label>
              <textarea
                value={bulkSkuText}
                onChange={(e) => setBulkSkuText(e.target.value)}
                placeholder={`Example:\nLyon 3 Door Sideboard,ABC-123\nFribo TV Cabinet,XYZ-456`}
                className="w-full h-48 p-3 border border-gray-200 rounded-lg text-sm font-mono"
                data-testid="bulk-sku-textarea"
              />
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-sm">
              <p className="font-medium mb-1">Instructions:</p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>One product per line</li>
                <li>Format: Product Name,SKU</li>
                <li>Product names must match exactly (case-insensitive)</li>
                <li>Duplicate SKUs will be skipped</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkSkuDialog(false)}>Cancel</Button>
            <Button onClick={handleBulkSkuAssign} disabled={bulkSkuUploading || !bulkSkuText.trim()} data-testid="confirm-bulk-sku-btn">
              {bulkSkuUploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload & Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Image URL Dialog */}
      <Dialog open={!!editingImageProduct} onOpenChange={(open) => !open && setEditingImageProduct(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="w-5 h-5" />
              Add Image URLs
            </DialogTitle>
            <DialogDescription>
              Add image URLs for: <span className="font-semibold">{editingImageProduct?.name}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Image URLs (one per line)</label>
              <textarea
                value={newImageUrls}
                onChange={(e) => setNewImageUrls(e.target.value)}
                placeholder={`https://example.com/image1.jpg\nhttps://example.com/image2.jpg`}
                className="w-full h-32 p-3 border border-gray-200 rounded-lg text-sm"
                data-testid="add-image-urls-textarea"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingImageProduct(null)}>Cancel</Button>
            <Button onClick={handleAddImages} disabled={addingImages || !newImageUrls.trim()} data-testid="confirm-add-images-btn">
              {addingImages ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Images
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV SKU Upload Dialog */}
      <Dialog open={showCsvSkuDialog} onOpenChange={(open) => !open && resetCsvDialog()}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#003366]" />
              Upload CSV to Match SKUs
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file with product names and SKUs. The system will match products by name and assign SKUs.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {!csvSkuJobId ? (
              <>
                {/* File Upload */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Select CSV File</label>
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center hover:border-[#003366] transition-colors">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleCsvFileSelect}
                      className="hidden"
                      id="csv-sku-upload"
                      data-testid="csv-file-input"
                    />
                    <label htmlFor="csv-sku-upload" className="cursor-pointer">
                      {csvFile ? (
                        <div className="flex items-center justify-center gap-2">
                          <FileText className="w-8 h-8 text-[#003366]" />
                          <div className="text-left">
                            <p className="font-medium text-gray-900">{csvFile.name}</p>
                            <p className="text-sm text-gray-500">{(csvFile.size / 1024).toFixed(1)} KB</p>
                          </div>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-600">Click to select CSV file</p>
                          <p className="text-xs text-gray-400 mt-1">Supports .csv files</p>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                {/* Column Mapping */}
                {csvColumns.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-sm font-medium">Map Columns</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Product Name Column</label>
                        <Select value={nameColumn} onValueChange={setNameColumn}>
                          <SelectTrigger data-testid="name-column-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {csvColumns.map(col => (
                              <SelectItem key={col} value={col}>{col}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">SKU Column</label>
                        <Select value={skuColumnSelect} onValueChange={setSkuColumnSelect}>
                          <SelectTrigger data-testid="sku-column-select-csv">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {csvColumns.map(col => (
                              <SelectItem key={col} value={col}>{col}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-sm">
                      <p className="font-medium mb-1">Detected {csvColumns.length} columns:</p>
                      <p className="text-gray-600 truncate">{csvColumns.join(", ")}</p>
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* Progress View */
              <div className="space-y-4">
                {/* Progress Bar */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Processing...</span>
                    <span className="font-medium">{csvSkuStatus?.progress_percent || 0}%</span>
                  </div>
                  <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-[#003366] to-[#0066cc] transition-all duration-300 ease-out"
                      style={{ width: `${csvSkuStatus?.progress_percent || 0}%` }}
                    />
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-4 gap-2">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-gray-900">{csvSkuStatus?.processed || 0}</p>
                    <p className="text-xs text-gray-500">Processed</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-emerald-600">{csvSkuStatus?.assigned || 0}</p>
                    <p className="text-xs text-emerald-700">Assigned</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-blue-600">{csvSkuStatus?.matched || 0}</p>
                    <p className="text-xs text-blue-700">Matched</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-orange-600">{csvSkuStatus?.not_found || 0}</p>
                    <p className="text-xs text-orange-700">Not Found</p>
                  </div>
                </div>

                {/* Additional Stats */}
                <div className="flex gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <span className="text-gray-500">Already has SKU:</span>
                    <span className="font-medium">{csvSkuStatus?.already_has_sku || 0}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-500">SKU exists:</span>
                    <span className="font-medium">{csvSkuStatus?.sku_exists || 0}</span>
                  </div>
                </div>

                {/* Status Badge */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Status:</span>
                  <Badge className={
                    csvSkuStatus?.status === "running" ? "bg-blue-500" :
                    csvSkuStatus?.status === "completed" ? "bg-emerald-500" :
                    csvSkuStatus?.status === "failed" ? "bg-red-500" : "bg-gray-500"
                  }>
                    {csvSkuStatus?.status === "running" && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                    {csvSkuStatus?.status?.charAt(0).toUpperCase() + csvSkuStatus?.status?.slice(1)}
                  </Badge>
                </div>

                {/* Current Item */}
                {csvSkuStatus?.current_item && csvSkuStatus?.status === "running" && (
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-xs text-blue-600 mb-1">Currently processing:</p>
                    <p className="text-sm font-medium text-blue-900 truncate">{csvSkuStatus.current_item}</p>
                  </div>
                )}

                {/* Recent Results */}
                {csvSkuStatus?.results?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Recent assignments:</p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {csvSkuStatus.results.slice(-5).reverse().map((r, i) => (
                        <div key={i} className="flex items-center justify-between text-sm bg-gray-50 px-3 py-2 rounded">
                          <span className="truncate max-w-[200px]">{r.name}</span>
                          <Badge variant={r.status === "assigned" ? "default" : "secondary"} className={r.status === "assigned" ? "bg-emerald-500" : ""}>
                            {r.status === "assigned" ? r.sku : r.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Errors */}
                {csvSkuStatus?.error_details?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-red-700 mb-2">Issues ({csvSkuStatus.error_details.length}):</p>
                    <div className="max-h-24 overflow-y-auto space-y-1">
                      {csvSkuStatus.error_details.slice(-3).map((e, i) => (
                        <div key={i} className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded flex justify-between">
                          <span className="truncate max-w-[180px]">{e.name}</span>
                          <span>{e.message}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            {!csvSkuJobId ? (
              <>
                <Button variant="outline" onClick={resetCsvDialog}>Cancel</Button>
                <Button 
                  onClick={handleCsvSkuUpload} 
                  disabled={!csvFile || csvColumns.length === 0}
                  data-testid="start-csv-upload-btn"
                  className="bg-[#003366] hover:bg-[#004488]"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Start Processing
                </Button>
              </>
            ) : (
              <Button 
                variant="outline" 
                onClick={resetCsvDialog}
              >
                {csvSkuStatus?.status === "running" ? "Hide" : "Close"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Merge Dialog */}
      <Dialog open={showBulkMergeDialog} onOpenChange={setShowBulkMergeDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Merge className="w-5 h-5 text-blue-600" />
              Bulk Merge Duplicates
            </DialogTitle>
            <DialogDescription>
              Review auto-selected primary products and merge {autoSelections.length} duplicate groups
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Merge Strategy */}
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium">Merge Strategy:</label>
              <Select value={bulkMergeStrategy} onValueChange={setBulkMergeStrategy}>
                <SelectTrigger className="w-64" data-testid="bulk-merge-strategy-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="keep_primary">Keep Primary (merge images only)</SelectItem>
                  <SelectItem value="merge_all">Merge All Data (fill empty fields)</SelectItem>
                  <SelectItem value="select_best">Select Best (use most complete)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Selection Criteria Info */}
            <div className="bg-blue-50 rounded-lg p-3 text-sm">
              <p className="font-medium text-blue-800 mb-1">Selection Criteria: {
                selectionCriteria === "oldest" ? "Keep Oldest" :
                selectionCriteria === "newest" ? "Keep Newest" :
                selectionCriteria === "most_images" ? "Keep Most Images" :
                "Keep Most Data"
              }</p>
              <p className="text-blue-700">The primary product in each group was auto-selected based on this criteria.</p>
            </div>
            
            {/* Preview List */}
            <div>
              <p className="text-sm font-medium mb-2">Groups to merge ({autoSelections.length}):</p>
              <div className="max-h-64 overflow-y-auto space-y-2 border rounded-lg p-2">
                {autoSelections.map((selection, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <Badge variant="outline" className="text-xs">{selection.count} items</Badge>
                      <span className="truncate max-w-[200px] font-medium">{selection._id}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Keep:</span>
                      <span className="text-xs text-emerald-600 truncate max-w-[150px]">{selection.primary_name}</span>
                      <span className="text-xs text-red-500">Delete: {selection.secondary_ids.length}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Summary */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="font-medium text-yellow-800">Summary</p>
              <p className="text-sm text-yellow-700">
                {autoSelections.reduce((sum, s) => sum + s.secondary_ids.length, 0)} products will be merged and deleted.
                {autoSelections.length} products will be kept as primary.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowBulkMergeDialog(false); setAutoSelections([]); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkMerge} 
              disabled={bulkMerging || autoSelections.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-bulk-merge-btn"
            >
              {bulkMerging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Merge className="w-4 h-4 mr-2" />}
              Merge {autoSelections.length} Groups
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
